import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { MonthlyHistoryStats } from '@types';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface UsageChartProps {
  data: MonthlyHistoryStats[];
  loading?: boolean;
}

export default function UsageChart({ data, loading = false }: UsageChartProps) {
  const chartRef = useRef<ChartJS<'line'>>(null);

  useEffect(() => {
    // Cleanup chart on unmount
    // Capture ref value at effect start to prevent cleanup issues
    const chart = chartRef.current;
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Usage Over Time
          </h3>
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse w-full h-full bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Prepare chart data from NIB history
  const labels = data.map(item => {
    // NIB history has month in format like "2024-01" or similar
    const parts = item.month.split('-');
    if (parts.length >= 2 && parts[0] && parts[1]) {
      const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    }
    return item.month;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Employees Processed',
        data: data.map(item => item.employees_processed),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Submissions',
        data: data.map(item => item.submissions),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y;
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Usage Over Time
          </h3>
          <div className="text-sm text-gray-500">Last {data.length} months</div>
        </div>
        <div className="h-80">
          <Line ref={chartRef} data={chartData} options={options} />
        </div>
      </div>
    </motion.div>
  );
}
